import { Spinner } from 'construct-ui';
import _ from 'lodash';
import moment from 'moment';
import SelectModelAliasSearchInputComponent from '../../../../components/model/select-model-alias-search-input.component';
import paginationComponent from '../../../../components/pagination/pagination.component';
import DynamicDateComponent from '../../../../components/report/dynamic-date.component';
import SingleSelectComponent from '../../../../components/single-select/single-select.component';
import { codeMasterType, DATE_TYPE_CODES, formats, formats as FORMATS } from '../../../../config/constants';
import codeMasterModel from '../../../../models/common/code-master.model';
import HHPDistyCoverageModel, { hhpDistyCoverageDownloadParamsMapping } from '../../../../models/reports/psi/hhp-daily-coverage.model';
import ViewBase from '../../../view-base.view';
import HHPDistyCoverageDetailDialog from './hhp-disty-coverage-detail.dialog.view';
import { apiStatusCodes } from '../../../../config/global-variables';

export default function HHPDistyCoverageView() {
  ViewBase.call(this);

  const SUPPORTED_DATES = ['W', 'M'];

  const DATE_TYPE_LIMITS = {
    [DATE_TYPE_CODES.WEEK]: 20,
    [DATE_TYPE_CODES.MONTH]: 5
  };

  const DEFAULT_FEILDS = {
    dateType: SUPPORTED_DATES[0],
    fromDate: moment().format(formats.WEEK_FORMAT),
    toDate: moment().format(formats.WEEK_FORMAT),
    coverageType: '',
    modelAliases: [],
    category: [],
    isExcelDownload: false
  }

  const DEFAULT_PAGINATION = {
    pageIndex: 1,
    pageSize: 10,
    totalCount: 0
  }

  const COVERAGE_TYPE_PERCENTAGE = 'PCT'

  let coverageTypeOptions = [];
  let totalRecord = {};
  let tableData = [];
  let dynamicColumns = [];
  let isLoadingTable = false;
  let isDownloadingFiles = [false, false, false];
  let searchFields = structuredClone(DEFAULT_FEILDS);
  let currentRequestParams = structuredClone(DEFAULT_FEILDS);
  let pagination = structuredClone(DEFAULT_PAGINATION);
  let selectedRecord;

  const returnAPIFormat = (date) => {
    switch (currentRequestParams.dateType) {
      case DATE_TYPE_CODES.WEEK:
        return moment(date, FORMATS.WEEK_FORMAT).format(FORMATS.API_WEEK_FORMAT);

      case DATE_TYPE_CODES.MONTH:
        return moment(date, FORMATS.MONTH_FORMAT).format(FORMATS.API_MONTH_FORMAT);

      default:
        return ''
    }
  }

  const requestParams = {
    get pageIndex() {
      return pagination.pageIndex;
    },

    get pageSize() {
      return pagination.pageSize;
    },

    get dateType() {
      return currentRequestParams.dateType;
    },

    get fromDate() {
      return returnAPIFormat(currentRequestParams.fromDate);
    },

    get toDate() {
      return returnAPIFormat(currentRequestParams.toDate);
    },

    get coverageType() {
      return currentRequestParams.coverageType;
    },

    get modelAliases() {
      return currentRequestParams.modelAliases.map(({ modelAlias }) => modelAlias).join('^');
    },

    get isExcelDownload() {
      return currentRequestParams.isExcelDownload;
    }
  }

  function resetSearchFields() {
    searchFields = structuredClone(DEFAULT_FEILDS)
  }

  function updateSearchFields(attr, value) {
    if (Array.isArray(searchFields[attr])) {
      searchFields[attr].splice(0, Infinity, ...value);
    } else {
      searchFields[attr] = value;
    }
  }

  let formatDynamicColumnName = (col) => {
    let week = col.substring(0, 6)
    if (currentRequestParams.coverageType === COVERAGE_TYPE_PERCENTAGE) {
      return week + ' (%)'
    }
    return week + ' (' + this.getText('LT00001168', 'Store Count') + ')'
  }

  let getCoverageTypes = () => {
    coverageTypeOptions.splice(0);
    codeMasterModel.getByCategoryV2({ category: codeMasterType.hhpDistyCoverageType })
      .then((result) => {
        result?.map(i => {
          coverageTypeOptions.push({ code: i.code, name: i.name })
        })
      })
      .catch((error) => {
        coverageTypeOptions.splice(0);
        this.handleError(error);
      })
  }

  let getTableData = () => {
    isLoadingTable = true;

    return HHPDistyCoverageModel.searchHHPDistyCoverage(requestParams)
      .then((result) => {
        if (!result) {
          tableData = [];
          return;
        }

        pagination.totalCount = result.total;
        if (pagination.pageIndex === 1) {
          [totalRecord, ...tableData] = result.data;
        } else tableData = result.data;
        let { ID, MD_CODE, 'Disty Name': DistyName, Salesman, Region, ChannelCount, ...restAttrs } = totalRecord;
        dynamicColumns = Object.keys(restAttrs).sort()
      })
      .catch((error) => {
        this.handleError(error);
      })
      .finally(() => {
        isLoadingTable = false;
      })
  }

  let search = (e) => {
    // Form validation
    if (!this.validateForm(e)) {
      return false;
    }

    pagination.pageIndex = DEFAULT_PAGINATION.pageIndex;
    currentRequestParams = structuredClone(searchFields)
    getTableData()

    return false;
  }

  let clearFilters = (e) => {
    e.preventDefault();
    let form = document.querySelector('.needs-validation');
    form.classList.remove('was-validated');
    resetSearchFields();
  }

  let getSearchFilters = () => {
    this.showLoading();

    let optionPromises = [
      getCoverageTypes()
    ];

    Promise.allSettled(optionPromises)
      .catch((error) => {
        this.handleError(error);
      })
      .finally(() => {
        this.hideLoading();
      })
  }

  let downloadExcel = (e, index, type = '', excelSheetNames = [''], fileName = '') => {
    e.preventDefault();
    if (isDownloadingFiles[index]) {
      return false;
    }

    isDownloadingFiles[index] = true;
    currentRequestParams.isExcelDownload = true;
    HHPDistyCoverageModel.downloadHHPDistyCoverage(hhpDistyCoverageDownloadParamsMapping[type].name, requestParams, excelSheetNames, fileName)
      .catch((error) => {
        if (error.code === apiStatusCodes.noContent) {
          this.showErrorMessage(this.getText("LT00000317", "No results found"))
        } else {
          this.handleError(error);
        }
      })
      .finally(() => {
        isDownloadingFiles[index] = false;
        currentRequestParams.isExcelDownload = false;
      });

    return false;
  }

  let onPageSizeChanged = (pageIndex, pageSize) => {
    pagination.pageIndex = pageIndex;
    pagination.pageSize = pageSize;
    getTableData();
  }

  let onPageChanged = (pageIndex) => {
    pagination.pageIndex = pageIndex;
    getTableData();
  }

  return {
    oninit: (vnode) => {
      getSearchFilters();
    },
    view: (vnode) => {
      return [
        // Search section
        m('.d-flex.flex-column.align-items-end.gap-2.mb-3',
          m('#search-section.card.w-100.mb-0',
            m('.card-body',
              m('form.row.g-2.align-items-end.needs-validation[novalidate]',
                {
                  onsubmit: search
                },
                m('.col-xxl-3.col-xl-4.col-lg-6.col-sm-6',
                  m(DynamicDateComponent, {
                    id: 'period',
                    selectedDateType: searchFields.dateType,
                    supportedDates: SUPPORTED_DATES,
                    fromDate: searchFields.fromDate,
                    toDate: searchFields.toDate,
                    required: true,
                    max: DATE_TYPE_LIMITS[searchFields.dateType],
                    onChange: (dateType, fromDate, toDate) => {
                      searchFields.dateType = dateType;
                      searchFields.fromDate = fromDate;
                      searchFields.toDate = toDate;
                    },
                  })
                ),
                m('.col-xxl-3.col-xl-4.col-lg-6.col-sm-6.position-relative',
                  m('label.form-label.mandatory[for=coverageType]', this.getText("LT00001169", "Coverage Type")),
                  m(SingleSelectComponent, {
                    id: 'coverageType',
                    keyOnly: true,
                    required: true,
                    items: coverageTypeOptions,
                    selectedItem: searchFields.coverageType,
                    onSelect: (selectedItems) => {
                      updateSearchFields('coverageType', selectedItems);
                    }
                  }),
                  m('.invalid-tooltip', this.getText('EM00000815', 'Please select Coverage Type!'))
                ),
                m('.col-xxl-3.col-xl-4.col-lg-6.col-sm-6',
                  m('label.form-label[for=modelAliases]', this.getText("LT00000717", "Model")),
                  m(SelectModelAliasSearchInputComponent, {
                    id: 'modelAliases',
                    multiple: true,
                    selectedItems: searchFields.modelAliases,
                    onSelect: (value) => {
                      searchFields.modelAliases = value;
                    }
                  }),
                ),

                // Search & clear buttons
                m('.col-12.d-flex.justify-content-end.gap-2',
                  m('button.btn.btn-primary[type="submit"]', {
                  }, this.getText("LT00000001", "Search")),
                  m('button.btn.btn-white', {
                    onclick: clearFilters
                  }, this.getText("LT00000031", "Clear")),
                ),
              ),
            )
          ),
        ),
        // Search section - end

        // Table section
        m('.card',
          m('.card-body',
            m(Spinner, {
              active: isLoadingTable,
              background: true,
              fill: true,
              size: 'xl',
            }),
            m('.d-flex.justify-content-end.mb-2',
              m('.input-group.w-auto',
                m('button.btn.btn-primary.dropdown-toggle.dropdown-toggle-split[type=button][data-bs-toggle=dropdown][arie-expanded=false]',
                  {
                    disabled: !tableData.length,
                  },
                  isDownloadingFiles.includes(true)
                    ? m('span.spinner-border.spinner-border-sm.me-2[role=status][aria-hidden=true]')
                    : m('i.bi.bi-file-earmark-arrow-down-fill'),
                  this.getText("LT00000492", "Download")
                ),
                m('ul.dropdown-menu',
                  m('li',
                    m('a.dropdown-item[href="javascript:void(0);"]', {
                      class: isDownloadingFiles[0] ? 'disabled' : '',
                      onclick: (e) => { downloadExcel(e, 0, 'SellthruCoverage', ["Sellthru Coverage"], "SellthruCoverage") }
                    },
                      isDownloadingFiles[0] &&
                      m('span.spinner-border.spinner-border-sm.text-secondary.me-2[role=status]'),
                      this.getText("LT00001170", "Sellthru Coverage")
                    )
                  ),
                  m('li',
                    m('a.dropdown-item[href="javascript:void(0);"]', {
                      class: isDownloadingFiles[1] ? 'disabled' : '',
                      onclick: (e) => { downloadExcel(e, 1, 'SellthruCoverageRaw', ["Sellthru Coverage Raw"], "SellthruCoverageRaw") }
                    },
                      isDownloadingFiles[1] &&
                      m('span.spinner-border.spinner-border-sm.text-secondary.me-2[role=status]'),
                      this.getText("LT00001171", "Sellthru Coverage Raw")
                    )
                  ),
                  m('li',
                    m('a.dropdown-item[href="javascript:void(0);"]', {
                      class: isDownloadingFiles[2] ? 'disabled' : '',
                      onclick: (e) => { downloadExcel(e, 2, 'SellthruRaw', ["Sellthru Raw"], "SellthruRaw") }
                    },
                      isDownloadingFiles[2] &&
                      m('span.spinner-border.spinner-border-sm.text-secondary.me-2[role=status]'),
                      this.getText("LT00001172", "Sellthru Raw")
                    )
                  ),
                ),
              ),
            ),
            m('.row',
              m('.col-12.p-0',
                m('.table-responsive',
                  m('table.table', [
                    m('thead.thead-bggrey.sticky-thead',
                      m('tr.align-middle', [
                        m('th[scope="col"]', this.getText("LT00001173", "Disty Code")),
                        m('th[scope="col"]', this.getText("LT00001174", "Disty Name")),
                        m('th[scope="col"]', this.getText("LT00001175", "Salesman")),
                        m('th[scope="col"]', this.getText("LT00000722", "Region")),
                        m('th[scope="col"]', this.getText("LT00001119", "Channel Count")),
                        dynamicColumns.map(col => m('th[scope="col"]', formatDynamicColumnName(col)))
                      ])
                    ),
                    m('tbody',
                      (tableData.length == 0) &&
                      m('tr.align-middle',
                        m('td[colspan=8]', this.getText("EM00000141", "No records found."))
                      ),
                      tableData.length > 0 &&
                      tableData.map((row, index) => [
                        m('tr.align-middle',
                          m('td', row.MD_CODE),
                          m('td', currentRequestParams.coverageType === COVERAGE_TYPE_PERCENTAGE
                            ? m('button.btn.btn-link.p-0',
                              {
                                onclick: () => {
                                  selectedRecord = row
                                }
                              },
                              row['Disty Name'])
                            : row['Disty Name']
                          ),
                          m('td', row.Salesman),
                          m('td', row.Region),
                          m('td', row.ChannelCount),
                          dynamicColumns.map(col => m('td', { style: Number(row[col]) <= 0 ? 'background: var(--red-danger)' : '' }, row[col]))
                        )
                      ]),
                      !_.isEmpty(totalRecord) &&
                      m('tr.align-middle',
                        { style: 'background: var(--bs-gray-400)' },
                        m('td.fw-bold', this.getText('LT00000046', 'Total')),
                        m('td', totalRecord['Disty Name']),
                        m('td', totalRecord.Salesman),
                        m('td', totalRecord.Region),
                        m('td', totalRecord.ChannelCount),
                        dynamicColumns.map(col => m('td', totalRecord[col]))
                      )
                    )
                  ])
                ),
                tableData.length > 0 &&
                m(".px-3",
                  m(paginationComponent, {
                    currentPage: pagination.pageIndex,
                    pageSize: pagination.pageSize,
                    totalItems: pagination.totalCount,
                    onPageChanged: onPageChanged,
                    onPageSizeChanged: onPageSizeChanged
                  })
                )
              )
            )
          )
        ),
        // Table section - end

        // Detail Dialog section - start

        !!selectedRecord &&
        m(HHPDistyCoverageDetailDialog,
          {
            isOpen: !!selectedRecord,
            onClose: () => {
              selectedRecord = null;
            },
            filters: structuredClone({ ...requestParams, distyCode: selectedRecord.MD_CODE, distyName: selectedRecord['Disty Name'] }),
          }
        ),
        this.getBaseViews()
      ]
    }
  }
}
